import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Timeline,
    TimelineBodyContainer,
    TimelineContainer,
    TimelineContainerDevider,
    TimelineDragSlider,
    TimelineHeader,
    TimelineScrollContainer,
    TimelineTrack,
    TimelineTrackContainer,
} from '../../../../CommonComponents/timeline/Timeline'
import { useTimeline } from '../../../../CommonComponents/timeline/Timeline'
import ScenarioFooter from '../footer/ScenarioFooter'
import './ScenarioTimelineContainer.scss'
import ScenarioTimelineHeader from './ScenarioTimelineHeader'
import { useActivePointOnScroll } from '../../../../CommonComponents/timeline/hooks/useActivePointProps'
import { useScrollBoundary } from '../../../../CommonComponents/timeline/hooks/useScrollBoundary'
import { TimeFrameInMinutes, TimeFrameOptions } from '../../../../CommonComponents/timeline/constant'
import { getNextTimeFrame } from '../../../../CommonComponents/timeline/utils'
import useForm from '../../hooks/useForm'
import { validateSameScheduleDetails, disabledTimelineResizableSlider } from '../../utils/validation';
import useScenarioResponse from '../../hooks/useScenarioResponse'
import useLoader from '../../hooks/useLoader'
import ScheduleEvents from './schedule/ScheduleEvents'
import FaultEvents from './fault/FaultEvents'
import SunExposers from './orbital-insights/SunExposers'
import OrbitIndicators from './orbital-insights/OrbitIndicators'
import OrbitalEvents from './orbital-insights/OrbitalEvents'
import useAssets from '../../hooks/useAssets'
import useSelectedGroupSatellite from '../../hooks/useSelectedGroupSatellite'
import { getAllOrbitNames } from '../../../../CommonComponents/orbital-insights/helperFunction'
import { OrbitalInsightsIconSvg, ScheduleIconSvg } from '../../../../CommonComponents/svgs/Svgs'
import useStepper from '../../hooks/useStepper'
import useOrbitAnimationDetails from '../../hooks/useOrbitAnimationDetails'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../constants'

const PresimPlaybackCursor: React.FC<{ epochTime: number; color?: string }> = ({ epochTime, color = '#f0f0f0' }) => {
    const { getFrameStyle, scrollAreaRef } = useTimeline()
    const { left } = useMemo(() => getFrameStyle(epochTime, epochTime, 0, 'number'), [epochTime, getFrameStyle])
    const prevLeftRef = useRef(left)
    // next left value at which a scroll is allowed (set after each scroll to avoid rapid-fire cancellations)
    const nextScrollThresholdRef = useRef(-Infinity)

    useEffect(() => {
        const container = scrollAreaRef?.current
        if (!container) return

        const movedForward = left > prevLeftRef.current
        prevLeftRef.current = left
        if (!movedForward) return                          // ignore pause / stop jumps
        if (left < nextScrollThresholdRef.current) return // previous scroll still in flight

        const scrollLeft = container.scrollLeft
        const clientWidth = container.clientWidth
        if (left > scrollLeft + clientWidth) {
            const target = Math.max(0, left - clientWidth * 0.3)
            container.scrollTo({ left: target, behavior: 'smooth' })
            // don't allow another scroll until the cursor would be off-screen again after this one
            nextScrollThresholdRef.current = target + clientWidth
        }
    }, [left, scrollAreaRef])

    return <div className="presim__playback__cursor" style={{ left: `${left}px`, background: color }} />
}

const ScenarioTimelineContainer = () => {
    const { updateScenarioStep } = useStepper()
    const { formData, setFormData } = useForm()
    const { loading, presimRunning } = useLoader()
    const { scenarioResponse } = useScenarioResponse()
    const { orbitAnimationDetails, getViewerCurrentTime } = useOrbitAnimationDetails()
    const { selectedGroupSatellite } = useSelectedGroupSatellite()
    const { orbitalInsights, orbitStartOption, setOrbitStartOption } = useAssets()


    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [selectedOrbit, setSelectedOrbit] = useState('Orbit 1');
    const [selectedFrame, setSelectedFrame] = useState(TimeFrameOptions[0]);
    const [viewerEpoch, setViewerEpoch] = useState<number>(0)

    const startAnimation = useMemo(() => orbitAnimationDetails.startAnimation, [orbitAnimationDetails.startAnimation])

    const selectedSatellite = useMemo(() => selectedGroupSatellite?.satelliteId, [selectedGroupSatellite?.satelliteId])
    const isPlayAnimationMode = useMemo(() => !!selectedSatellite && startAnimation === selectedSatellite, [selectedSatellite, startAnimation])
    const selectedOrbitalInsights = useMemo(() => (selectedSatellite && orbitalInsights?.data) ? orbitalInsights?.data[selectedSatellite] : null, [orbitalInsights.data, selectedSatellite])
    const Orbits = useMemo(() => getAllOrbitNames(selectedOrbitalInsights, orbitStartOption).filter((orbit) => orbit !== 'All Orbits'), [selectedOrbitalInsights, orbitStartOption])

    const orbitExpandedView = useMemo(() => orbitAnimationDetails.expandedView, [orbitAnimationDetails.expandedView])
    const scenarioDeploymentType = useMemo(() => scenarioResponse?.scenarioDeploymentType, [scenarioResponse?.scenarioDeploymentType])

    const scenarioStartTime = useMemo(() => scenarioResponse?.scenarioStartTime || 0, [scenarioResponse?.scenarioStartTime])
    const scenarioDuration = useMemo(() => scenarioResponse?.scenarioDuration || 0, [scenarioResponse?.scenarioDuration])
    const scenarioEndTime = useMemo(() => scenarioStartTime + (scenarioDuration * 60), [scenarioStartTime, scenarioDuration])

    const simulationStartTime = useMemo(() => formData?.schedule?.simulationStartTime || 0, [formData?.schedule?.simulationStartTime])
    const simulationDuration = useMemo(() => formData?.schedule?.simulationDuration || 0, [formData?.schedule?.simulationDuration])
    const simulationEndTime = useMemo(() => simulationStartTime + (simulationDuration * 60), [simulationStartTime, simulationDuration])
    const loader = useMemo(() => loading?.execution === 'Generating' || loading?.execution === 'Plotting', [loading])

    const disabledResizableSlider = useMemo(() => disabledTimelineResizableSlider(formData, scenarioResponse) || startAnimation === selectedSatellite || presimRunning, [formData, scenarioResponse, startAnimation, selectedSatellite, presimRunning])

    const openScenario = useMemo(() => scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER, [scenarioDeploymentType])

    const onOrbitChange = (orbit: string) => {
        const orbitIndex = Number(orbit.replace(/\D/g, "") || 1)
        const selectedOrbit = `orbit_${orbitIndex}`
        const orbitPosition = document.getElementById(selectedOrbit)
        if (!scrollAreaRef.current || !orbitPosition) return
        scrollAreaRef.current.scrollLeft = orbitPosition.offsetLeft
        setSelectedOrbit(orbit);
    }

    useActivePointOnScroll({
        scrollAreaRef: scrollAreaRef,
        pointSelector: ".orbit__point",
        selectedFrame: selectedFrame,
        onActiveChange: (visiblePoints) => {
            if (!visiblePoints || visiblePoints.length === 0) return;
            // visiblePoints are sorted by left; first and last define the visible range
            if (visiblePoints.length === 1) {
                const id = visiblePoints[0].id; // e.g. 'orbit_1'
                const numMatch = id.match(/\d+/);
                const num = numMatch ? numMatch[0] : '';
                if (!num) return;
                setSelectedOrbit(`Orbit ${num}`);
                return;
            }

            const firstId = visiblePoints[0].id;
            const lastId = visiblePoints[visiblePoints.length - 1].id;
            const firstMatch = firstId.match(/\d+/);
            const lastMatch = lastId.match(/\d+/);
            if (!firstMatch || !lastMatch) return;
            const firstNum = firstMatch[0];
            const lastNum = lastMatch[0];
            // Range display: Orbit X-Y
            setSelectedOrbit(`Orbit ${firstNum}-${lastNum}`);
        }
    });

    useScrollBoundary({
        scrollAreaRef: scrollAreaRef,
        direction: "horizontal",
        onVerticalScroll: (scroll) => {
            setSelectedFrame((prev) => {
                // map current label -> minutes -> next minutes -> label
                const currentMinutes = TimeFrameInMinutes[prev as keyof typeof TimeFrameInMinutes] ?? TimeFrameInMinutes[TimeFrameOptions[0]]
                const nextMinutes = getNextTimeFrame(currentMinutes, scroll)
                const nextOption = TimeFrameOptions.find(option => TimeFrameInMinutes[option as keyof typeof TimeFrameInMinutes] === nextMinutes)
                return nextOption ?? prev
            })
        }
    });

    const onChangeFormData = (value: { max: number, min: number }) => {
        const { max, min } = value
        const duration = (max - min)
        if (duration < 10) return
        setFormData((prev) => {
            const schedule = {
                ...prev.schedule,
                simulationStartTime: min * 60,
                simulationDuration: duration
            }
            const status = prev.assets.schedule.data ? validateSameScheduleDetails(schedule, scenarioResponse!) ? 'completed' : 'invalidated' : prev.assets.schedule.status
            const assets = {
                ...prev.assets,
                schedule: {
                    ...prev.assets.schedule,
                    status: status
                }
            }
            return {
                ...prev,
                schedule: schedule,
                assets: assets
            }

        })
        updateScenarioStep('schedule')
    }

    const onSelectFrame = (frame: string) => {
        const value = TimeFrameInMinutes[frame]
        setSelectedFrame(frame)
        if ([30, 60].includes(value) && selectedOrbit.includes('-') && scrollAreaRef.current) {
            scrollAreaRef.current.scrollLeft = 0
            setSelectedOrbit('Orbit 1')
        }
    }

    useEffect(() => {
        if (!isPlayAnimationMode) return
        let rafId: number | null = null
        const tick = () => {
            const now = getViewerCurrentTime?.() || 0
            setViewerEpoch((prev) => (prev === now ? prev : now))
            rafId = requestAnimationFrame(tick)
        }
        tick()
        return () => {
            if (rafId) cancelAnimationFrame(rafId)
            // scroll back to start when animation stops
            if (scrollAreaRef.current) {
                scrollAreaRef.current.scrollTo({ left: 0, behavior: 'smooth' })
            }
        }
    }, [getViewerCurrentTime, isPlayAnimationMode])

    const clampedViewerEpoch = useMemo(() => {
        const fallback = simulationStartTime || scenarioStartTime
        if (!viewerEpoch) return fallback
        const min = simulationStartTime || scenarioStartTime
        const max = simulationEndTime || scenarioEndTime
        if (viewerEpoch < min) return min
        if (viewerEpoch > max) return max
        return viewerEpoch
    }, [simulationEndTime, simulationStartTime, scenarioEndTime, scenarioStartTime, viewerEpoch])

    return (
        <div className='scenario__timelineContainer'>
            <ScenarioFooter />
            <Timeline
                scrollAreaRef={scrollAreaRef}
                timelineDuration={scenarioDuration}
                timelineStartTime={scenarioStartTime}
                timelineEndTime={scenarioEndTime}
                selectedFrame={TimeFrameInMinutes[selectedFrame]}
                loading={loader}
                show={!openScenario && Orbits.length > 0 && !orbitExpandedView}
                hideToggleButton={openScenario}
            >
                <TimelineHeader>
                    <ScenarioTimelineHeader
                        Orbits={Orbits}
                        onOrbitChange={onOrbitChange}
                        selectedOrbit={selectedOrbit}
                        selectedFrame={selectedFrame}
                        onTimeFrameChange={onSelectFrame}
                    />
                </TimelineHeader>
                <TimelineBodyContainer>
                    <TimelineScrollContainer>
                        <TimelineContainer className='z-9'>
                            <TimelineTrackContainer>
                                <TimelineTrack />
                                <TimelineDragSlider
                                    disabled={disabledResizableSlider}
                                    value={{
                                        max: simulationEndTime / 60,
                                        min: simulationStartTime / 60
                                    }}
                                    onChange={(value) => {
                                        onChangeFormData(value)
                                    }}
                                />
                                {isPlayAnimationMode && <PresimPlaybackCursor epochTime={clampedViewerEpoch} />}
                                <SunExposers />
                                <OrbitIndicators />
                                <FaultEvents />
                            </TimelineTrackContainer>
                        </TimelineContainer>
                        <TimelineContainer
                            sidebarDetails={{
                                name: 'Orbital Insights',
                                icon: <OrbitalInsightsIconSvg />
                            }}
                        >
                            <OrbitalEvents />
                        </TimelineContainer>
                        <TimelineContainerDevider />
                        <TimelineContainer
                            sidebarDetails={{
                                name: 'Onboard Schedule',
                                icon: <ScheduleIconSvg />
                            }}
                        >
                            <ScheduleEvents />
                        </TimelineContainer>
                    </TimelineScrollContainer>
                </TimelineBodyContainer>
            </Timeline>
        </div>
    )
}

export default ScenarioTimelineContainer